/**
===================================================
Module: frontend.src.components.Login.tsx
Description: Login and registration component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { apiService } from "../services/api";
import { RegisterRequest } from "../types";
import NotificationSystem, { Notification } from "./NotificationSystem";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "../styles/login.css";
import {
  parsePhoneNumber,
  isValidPhoneNumber,
  CountryCode,
  parsePhoneNumberFromString,
} from "libphonenumber-js";

const Login: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [departmentName, setDepartmentName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Add notification function
  const addNotification = (
    message: string,
    type: "success" | "error" | "info",
    duration?: number,
  ) => {
    const id = Date.now().toString();
    const notification: Notification = {
      id,
      message,
      type,
      duration: duration || 5000,
    };
    setNotifications((prev) => [...prev, notification]);
  };

  // Remove notification function
  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  };

  // Handle URL parameters for verification messages
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get("message");
    const errorParam = urlParams.get("error");

    if (message === "verified") {
      addNotification(
        "メール認証が完了しました",
        "success",
      );
    } else if (message === "already_verified") {
      addNotification("既に認証が完了しています", "info");
    } else if (message === "password_reset_success") {
      addNotification(
        "パスワードをリセットしました",
        "success",
      );
    } else if (errorParam === "invalid_token") {
      addNotification("メール認証のリンクが無効です。もう一度リンクを送信してください", "error");
    } else if (errorParam === "verification_failed") {
      addNotification("メール認証に失敗しました。時間をおいてもう一度お試しください", "error");
    } else if (errorParam === "server_error") {
      addNotification(
        "メール認証に失敗しました。時間をおいてもう一度お試しください",
        "error",
      );
    } else if (errorParam === "user_not_found") {
      addNotification("アカウントが見つかりませんでした。再度、登録してください", "error");
    }
  }, [location]);

  // Add validation state
  const [phoneError, setPhoneError] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>("JP");

  // Helper function to convert to E.164 format
  const toE164 = (raw: string) => (raw?.startsWith("+") ? raw : `+${raw}`);

  // Add validation function using libphonenumber-js properly
  const validatePhoneNumber = (raw: string) => {
    if (!raw || raw.trim() === "") {
      setPhoneError("電話番号は必須です");
      return false;
    }

    const e164 = toE164(raw);
    try {
      if (!isValidPhoneNumber(e164)) {
        setPhoneError("有効な電話番号を入力してください");
        return false;
      }
      // Optional: normalize to canonical E.164
      const parsed = parsePhoneNumberFromString(e164);
      // setPhoneNumber(parsed?.number ?? raw); // uncomment if you want to overwrite with E.164
      setPhoneError("");
      return true;
    } catch {
      setPhoneError("有効な電話番号を入力してください");
      return false;
    }
  };

  // Validate gently while typing; be stricter onBlur/submit
  const handlePhoneChange = (
    value: string,
    country: any,
    _e: any,
    _formattedValue: string,
  ) => {
    setPhoneNumber(value);
    const iso2 = (country?.countryCode ?? "jp").toUpperCase() as CountryCode;
    setSelectedCountry(iso2);

    // Avoid early errors: wait until user has a few digits beyond the dial code
    const digits = value.replace(/\D/g, "");
    const dial = (country?.dialCode as string) || "";
    if (digits.length >= dial.length + 4) {
      validatePhoneNumber(value);
    } else {
      setPhoneError("");
    }
  };

  const validateRegistrationForm = (): boolean => {
    if (!firstName.trim()) {
      addNotification("名は入力必須です.", "error");
      return false;
    }

    if (!lastName.trim()) {
      addNotification("姓は入力必須です", "error");
      return false;
    }

    if (!email.trim()) {
      addNotification("メールアドレスは入力必須です", "error");
      return false;
    }

    if (!companyName.trim()) {
      addNotification("会社名は入力必須です", "error");
      return false;
    }

    if (!departmentName.trim()) {
      addNotification("部署名は入力必須です", "error");
      return false;
    }

    if (!phoneNumber.trim()) {
      addNotification("電話番号は入力必須です", "error");
      return false;
    }

    if (!address.trim()) {
      addNotification("所在地は入力必須です", "error");
      return false;
    }

    if (!password.trim()) {
      addNotification("パスワードは入力必須です", "error");
      return false;
    }

    if (password.length < 8) {
      addNotification("パスワードは８文字以上にしてください", "error");
      return false;
    }

    if (!confirmPassword.trim()) {
      addNotification("パスワードの確認は入力必須です", "error");
      return false;
    }

    if (password !== confirmPassword) {
      addNotification(
        "パスワードが一致していません",
        "error",
      );
      return false;
    }

    if (!agreedToTerms) {
      addNotification(
        "利用規約への同意が必要です",
        "error",
      );
      return false;
    }

    return true;
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login({ username, password });
      navigate("/projects");
    } catch (error: any) {
      addNotification(
        error.message || "ログインに失敗しました。ユーザー名またはパスワードを確認してください",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateRegistrationForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const registrationData: RegisterRequest = {
        email,
        password,
        ...(firstName && { first_name: firstName }),
        ...(lastName && { last_name: lastName }),
        company_name: companyName,
        ...(branchName && { branch_name: branchName }),
        department_name: departmentName,
        ...(phoneNumber && { phone_number: phoneNumber }),
        address,
        agreed_to_tos: agreedToTerms,
      };

      const response = await apiService.register(registrationData);

      addNotification(response.message, "success", 8000);

      // Clear form
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setFirstName("");
      setLastName("");
      setCompanyName("");
      setBranchName("");
      setDepartmentName("");
      setPhoneNumber("");
      setAddress("");

      // Switch to login form after a delay
      setTimeout(() => {
        setIsLogin(true);
      }, 3000);
    } catch (error: any) {
      addNotification(
        error.message || "アカウント登録に失敗しました。時間をおいてもう一度お試しください",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail.trim()) {
      addNotification("メールアドレスは入力必須です", "error");
      return;
    }

    setIsLoading(true);

    try {
      const response = await apiService.forgotPassword(forgotPasswordEmail);
      addNotification(response.message, "success", 8000);

      // Clear form and hide modal
      setForgotPasswordEmail("");
      setShowForgotPassword(false);
    } catch (error: any) {
      addNotification(
        error.message ||
          "パスワードリセットのリンクの送信に失敗しました。時間をおいてもう一度お試しください",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setShowForgotPassword(false);
    // Clear form fields
    setUsername("");
    setPassword("");
    setConfirmPassword("");
    setEmail("");
    setFirstName("");
    setLastName("");
    setCompanyName("");
    setBranchName("");
    setDepartmentName("");
    setPhoneNumber("");
    setAddress("");
    setForgotPasswordEmail("");
    setAgreedToTerms(false);
    // Clear any existing notifications
    setNotifications([]);
  };

  return (
    <>
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      <div className="login-container">
        <div className="left-panel">
          {/* Clean background with just the cover.png image */}
        </div>

        <div className="right-panel">
          <div className="form-container">
            {showForgotPassword ? (
              // Forgot Password Form
              <>
                <div className="form-header">
                  <div className="header-icon">
                    <img src="/logo.png" alt="KTS" />
                  </div>
                  <h2>パスワードのリセット</h2>
                  <p>パスワード再設定用のリンクを送信します。メールアドレスを入力してください。 </p>
                </div>

                <form
                  className="login-form"
                  onSubmit={handleForgotPasswordSubmit}
                >
                  <div className="form-content">
                    <div className="form-group">
                      <label htmlFor="forgotPasswordEmail">メールアドレス</label>
                      <div className="input-wrapper">
                        <div className="input-icon">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <polyline
                              points="22,6 12,13 2,6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                        <input
                          type="email"
                          id="forgotPasswordEmail"
                          name="forgotPasswordEmail"
                          value={forgotPasswordEmail}
                          onChange={(e) =>
                            setForgotPasswordEmail(e.target.value)
                          }
                          required
                          autoComplete="email"
                          placeholder="メールアドレス"
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="login-button"
                    disabled={isLoading}
                  >
                    <div className="button-content">
                      {isLoading && <div className="spinner"></div>}
                      <span>
                        {isLoading
                          ? "パスワードリセットのリンクを送信中..."
                          : "パスワードリセットのリンクを送信"}
                      </span>
                    </div>
                  </button>

                  <div className="form-toggle">
                    <button
                      type="button"
                      className="toggle-link"
                      onClick={() => setShowForgotPassword(false)}
                      disabled={isLoading}
                      >
                      ログイン画面に戻る
                    </button>
                  </div>
                </form>
              </>
            ) : (
              // Main Login/Register Form
              <>
                <div className="form-header">
                  <div className="header-icon">
                    <img src="/logo.png" alt="KTS" />
                  </div>
                  <h2>{isLogin ? "3Dマップ生成 ログイン" : "アカウント登録"}</h2>
                </div>

                <form
                  className="login-form"
                  onSubmit={isLogin ? handleLoginSubmit : handleRegisterSubmit}
                >
                  <div className="form-content">
                    {isLogin ? (
                      // Login Form
                      <>
                        <div className="form-group">
                          <label htmlFor="username">メールアドレス</label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <polyline
                                  points="22,6 12,13 2,6"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              id="username"
                              name="username"
                              value={username}
                              onChange={(e) => setUsername(e.target.value)}
                              required
                              autoComplete="username"
                              placeholder="メールアドレス"
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="password">パスワード</label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                  ry="2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="16"
                                  r="1"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="password"
                              id="password"
                              name="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              autoComplete="current-password"
                              placeholder="パスワード"
                            />
                          </div>
                        </div>

                        <div className="form-actions">
                          <button
                            type="button"
                            className="forgot-password-btn"
                            onClick={() => setShowForgotPassword(true)}
                            disabled={isLoading}
                          >
                            パスワードをお忘れですか?
                          </button>
                        </div>
                      </>
                    ) : (
                      // Register Form
                      <>
                        {/* Last Name (★) + First Name (★) — Japanese order */}
                        <div className="form-row">
                          <div className="form-group form-group-half">
                            <label htmlFor="lastName">姓<span className="required-star">*</span></label>
                            <div className="input-wrapper">
                              <div className="input-icon">
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <circle
                                    cx="12"
                                    cy="7"
                                    r="4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                required
                                autoComplete="family-name"
                                placeholder="姓"
                              />
                            </div>
                          </div>

                          <div className="form-group form-group-half">
                            <label htmlFor="firstName">名<span className="required-star">*</span></label>
                            <div className="input-wrapper">
                              <div className="input-icon">
                                <svg
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                  <circle
                                    cx="12"
                                    cy="7"
                                    r="4"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                              <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                required
                                autoComplete="given-name"
                                placeholder="名"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Email Address (★) */}
                        <div className="form-group">
                          <label htmlFor="email">メールアドレス<span className="required-star">*</span></label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <polyline
                                  points="22,6 12,13 2,6"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              required
                              autoComplete="email"
                              placeholder="メールアドレス"
                            />
                          </div>
                        </div>

                        {/* Company Name (★) */}
                        <div className="form-group">
                          <label htmlFor="companyName">会社名<span className="required-star">*</span></label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3 21H21M3 7V21M21 7V21M7 11H9M7 15H9M15 11H17M15 15H17M12 21V17H8V21M6 7H18L12 3L6 7Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              id="companyName"
                              name="companyName"
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              required
                              placeholder="会社名"
                            />
                          </div>
                        </div>

                        {/* Branch Name */}
                        <div className="form-group">
                          <label htmlFor="branchName">支店名</label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3 21H21M3 7V21M21 7V21M7 11H9M7 15H9M15 11H17M15 15H17M12 21V17H8V21M6 7H18L12 3L6 7Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              id="branchName"
                              name="branchName"
                              value={branchName}
                              onChange={(e) => setBranchName(e.target.value)}
                              placeholder="支店名"
                            />
                          </div>
                        </div>

                        {/* Department Name (★) */}
                        <div className="form-group">
                          <label htmlFor="departmentName">
                            部署名<span className="required-star">*</span>
                          </label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21M23 21V19C22.9986 17.1771 21.765 15.5857 20 15.13M16 3.13C17.7699 3.58317 19.0078 5.17799 19.0078 7.005C19.0078 8.83201 17.7699 10.4268 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              id="departmentName"
                              name="departmentName"
                              value={departmentName}
                              onChange={(e) => setDepartmentName(e.target.value)}
                              required
                              placeholder="部署名"
                            />
                          </div>
                        </div>

                        {/* Phone Number (★) */}
                        <div className="form-group">
                          <label htmlFor="phoneNumber">電話番号<span className="required-star">*</span></label>
                          <div className="input-wrapper">
                            <PhoneInput
                              country={"jp"}
                              value={phoneNumber}
                              onChange={handlePhoneChange}
                              inputClass="phone-input-field"
                              containerClass="phone-input-container"
                              buttonClass="phone-input-button"
                              enableSearch
                              dropdownClass="phone-input-dropdown"
                              searchPlaceholder="Search country"
                              inputProps={{
                                name: "phoneNumber",
                                id: "phoneNumber",
                                autoComplete: "tel",
                                placeholder: "電話番号",
                                onBlur: () => validatePhoneNumber(phoneNumber),
                              }}
                            />
                          </div>
                          {phoneError && (
                            <div className="input-error">{phoneError}</div>
                          )}
                        </div>

                        {/* Address (★) */}
                        <div className="form-group">
                          <label htmlFor="address">所在地<span className="required-star">*</span></label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="10"
                                  r="3"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="text"
                              id="address"
                              name="address"
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                              required
                              autoComplete="street-address"
                              placeholder="所在地"
                            />
                          </div>
                        </div>

                        {/* Password (★) */}
                        <div className="form-group">
                          <label htmlFor="registerPassword">パスワード<span className="required-star">*</span></label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                  ry="2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="16"
                                  r="1"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="password"
                              id="registerPassword"
                              name="password"
                              value={password}
                              onChange={(e) => setPassword(e.target.value)}
                              required
                              autoComplete="new-password"
                              placeholder="パスワードを入力してください(８文字以上)"
                              minLength={8}
                            />
                          </div>
                        </div>

                        {/* Confirm Password (★) */}
                        <div className="form-group">
                          <label htmlFor="confirmPassword">
                            パスワードの確認<span className="required-star">*</span>
                          </label>
                          <div className="input-wrapper">
                            <div className="input-icon">
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                  ry="2"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <circle
                                  cx="12"
                                  cy="16"
                                  r="1"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </div>
                            <input
                              type="password"
                              id="confirmPassword"
                              name="confirmPassword"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              required
                              autoComplete="new-password"
                              placeholder="パスワードを再入力してください"
                              minLength={8}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {!isLogin && (
                    <div className="tos-checkbox-group">
                      <label className="tos-label" htmlFor="agreedToTerms">
                        <input
                          type="checkbox"
                          id="agreedToTerms"
                          checked={agreedToTerms}
                          onChange={(e) => setAgreedToTerms(e.target.checked)}
                          className="tos-checkbox"
                        />
                        <span className="tos-checkmark"></span>
                        <span className="tos-text">
                          <a
                            href="https://doc.open.kts.co.jp/legal/terms.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tos-link"
                          >
                            利用規約
                          </a>
                          {" "}に同意します
                        </span>
                      </label>
                    </div>
                  )}

                  <button
                    type="submit"
                    className="login-button"
                    disabled={isLoading || (!isLogin && !agreedToTerms)}
                  >
                    <div className="button-content">
                      {isLoading && <div className="spinner"></div>}
                      <span>
                        {isLoading
                          ? isLogin
                            ? "ログイン中..."
                            : "登録中..."
                          : isLogin
                            ? "ログイン"
                            : "登録"}
                      </span>
                    </div>
                  </button>

                  <div className="form-toggle">
                    <p>
                      {isLogin
                        ? "アカウント作成は"
                        : "アカウントをお持ちの場合 "}
                      <button
                        type="button"
                        className="toggle-link"
                        onClick={toggleMode}
                        disabled={isLoading}
                      >
                        {isLogin ? "こちら" : "ログイン"}
                      </button>
                    </p>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
